import { Controller, useFieldArray } from 'react-hook-form';
import { DEFAULT_SYNTHETIC_ASSETS } from '../../../data/returnData';
import { Field, FieldRow } from '../../../forms/fields';
import type { SectionProps } from '../sectionProps';

/**
 * GLIDE-PATHS editor (PLAN.md M7 Strategy — named allocation schedules referenced by accounts'
 * `glidePathRef`). Each glide path has an `id` (the ref key), a `name`, and ordered `stops` — each
 * stop an `{ at: TimePoint, allocation }`. The simulator holds a stop's allocation until the next
 * stop. Stops are anchored to a CALENDAR YEAR here (the common de-risking-toward-a-year case);
 * event-relative anchors remain valid in the schema but are out of scope for this MVP control.
 *
 * The stop `allocation` reuses the same record-of-weights UX as the account allocation editor, with
 * a `Controller` owning each stop's allocation map so add/remove/rename stay atomic.
 */

export interface GlidePathsEditorProps {
  form: SectionProps['form'];
}

const KNOWN_ASSET_IDS = DEFAULT_SYNTHETIC_ASSETS.map((a) => a.id);

export function GlidePathsEditor({ form }: GlidePathsEditorProps): React.ReactElement {
  const { register, control, formState } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'glidePaths' });
  const errors = formState.errors.glidePaths;
  const startYear = form.getValues('startYear');

  return (
    <div>
      {fields.map((f, gi) => {
        const rowErrors = errors?.[gi];
        return (
          <div key={f.id} className="card card--sunken" style={{ marginBottom: 'var(--sp-3)' }}>
            <FieldRow>
              <div style={{ flex: 1, minWidth: 130 }}>
                <Field label="Id" error={rowErrors?.id} htmlFor={`glidePaths.${gi}.id`}>
                  <input
                    id={`glidePaths.${gi}.id`}
                    className="input"
                    {...register(`glidePaths.${gi}.id`)}
                  />
                </Field>
              </div>
              <div style={{ flex: 2, minWidth: 160 }}>
                <Field label="Name" error={rowErrors?.name} htmlFor={`glidePaths.${gi}.name`}>
                  <input
                    id={`glidePaths.${gi}.name`}
                    className="input"
                    {...register(`glidePaths.${gi}.name`)}
                  />
                </Field>
              </div>
            </FieldRow>

            <GlidePathStops form={form} glideIndex={gi} startYear={startYear} />

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--sp-2)' }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => remove(gi)}
                aria-label={`Remove glide path ${gi + 1}`}
              >
                Remove glide path
              </button>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className="btn"
        onClick={() =>
          append({
            id: `glide${fields.length + 1}`,
            name: '',
            stops: [{ at: { kind: 'year', year: startYear }, allocation: { cash: 1 } }],
          })
        }
      >
        + Add glide path
      </button>
    </div>
  );
}

interface StopsProps {
  form: SectionProps['form'];
  glideIndex: number;
  startYear: number;
}

function GlidePathStops({ form, glideIndex, startYear }: StopsProps): React.ReactElement {
  const { control } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: `glidePaths.${glideIndex}.stops`,
  });

  return (
    <div className="glide__stops">
      {fields.map((s, si) => (
        <div key={s.id} className="glide__stop">
          <FieldRow>
            <div style={{ flex: 1, minWidth: 120 }}>
              <Controller
                control={control}
                name={`glidePaths.${glideIndex}.stops.${si}.at`}
                render={({ field }) => {
                  const at = field.value as { kind: 'year'; year: number } | { kind: 'event' };
                  const year = at.kind === 'year' ? at.year : startYear;
                  return (
                    <Field label="From year" htmlFor={`glide.${glideIndex}.stop.${si}.year`}>
                      <input
                        id={`glide.${glideIndex}.stop.${si}.year`}
                        className="input input--num"
                        type="number"
                        value={Number.isNaN(year) ? '' : year}
                        onBlur={field.onBlur}
                        onChange={(e) =>
                          field.onChange({ kind: 'year', year: e.currentTarget.valueAsNumber })
                        }
                      />
                    </Field>
                  );
                }}
              />
            </div>
          </FieldRow>

          <Controller
            control={control}
            name={`glidePaths.${glideIndex}.stops.${si}.allocation`}
            render={({ field }) => {
              const record = (field.value as Record<string, number> | undefined) ?? {};
              const entries = Object.entries(record);
              const setEntry = (k: string, w: number): void =>
                field.onChange({ ...record, [k]: w });
              const renameEntry = (oldK: string, newK: string): void => {
                if (newK === oldK) return;
                const next: Record<string, number> = {};
                for (const [k, v] of entries) next[k === oldK ? newK : k] = v;
                field.onChange(next);
              };
              const removeEntry = (k: string): void => {
                const next = { ...record };
                delete next[k];
                field.onChange(next);
              };
              const addEntry = (): void => {
                const candidate = KNOWN_ASSET_IDS.find((id) => !(id in record)) ?? '';
                field.onChange({ ...record, [candidate]: 0 });
              };
              return (
                <Field label="Allocation" hint="Weights normalise.">
                  <div className="alloc">
                    {entries.map(([key, weight]) => (
                      <div key={key} className="alloc__row">
                        <input
                          className="input alloc__asset"
                          aria-label="Asset class id"
                          list="alloc-asset-ids"
                          value={key}
                          onChange={(e) => renameEntry(key, e.currentTarget.value)}
                        />
                        <input
                          className="input input--num alloc__weight"
                          type="number"
                          step="0.05"
                          min={0}
                          aria-label={`${key} weight`}
                          value={Number.isNaN(weight) ? '' : weight}
                          onChange={(e) => setEntry(key, e.currentTarget.valueAsNumber)}
                        />
                        <button
                          type="button"
                          className="btn btn--ghost alloc__remove"
                          onClick={() => removeEntry(key)}
                          aria-label={`Remove ${key}`}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button type="button" className="btn alloc__add" onClick={addEntry}>
                      + Add asset class
                    </button>
                  </div>
                </Field>
              );
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => remove(si)}
              disabled={fields.length <= 1}
              aria-label={`Remove stop ${si + 1}`}
            >
              Remove stop
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="btn"
        onClick={() => append({ at: { kind: 'year', year: startYear }, allocation: { cash: 1 } })}
      >
        + Add stop
      </button>
    </div>
  );
}
